/**
 * Classic scheme aiming (08 §3, 01 §5). The ranger moves in 8 directions and aims in 32: the
 * aim is his facing, four steps to every 45 degrees, and it comes round toward whichever way the
 * stick is pushed. A light tap turns him one step without taking a pace, which is how the 24
 * in-between directions are reached; he turns to face before he walks. Pure, one update per tick.
 */

export const AIM_STEPS = 32;
const STEP_ANGLE = (Math.PI * 2) / AIM_STEPS;
/** A push shorter than this is a tap: about 120 ms at 60 ticks a second. */
export const TAP_TICKS = 7;
/** Once turning, one step every this many ticks: about half a second to face about. */
export const TURN_EVERY_TICKS = 2;
/** He steps off once he is within this many steps (45 degrees) of where he is going. */
export const WALK_WITHIN_STEPS = 4;

/** Half-angle of the aim-assist cone that makes up for 32-step aiming (08 §3): 10 degrees. */
export const CLASSIC_AIM_ASSIST = Math.PI / 18;

export type Axis = -1 | 0 | 1;

/** Aim step of each stick direction: 0 = south (+z), 8 = east (+x), 16 = north, 24 = west. */
export function stepOfDirection(dx: Axis, dz: Axis): number | null {
  if (dx === 0 && dz === 0) return null;
  if (dx === 0) return dz > 0 ? 0 : 16;
  if (dz === 0) return dx > 0 ? 8 : 24;
  if (dx > 0) return dz > 0 ? 4 : 12;
  return dz > 0 ? 28 : 20;
}

/** Signed steps from `from` to `to` the short way round, in (-16, 16]; dead astern turns clockwise. */
export function stepsBetween(from: number, to: number): number {
  const d = (((to - from) % AIM_STEPS) + AIM_STEPS) % AIM_STEPS;
  return d > AIM_STEPS / 2 ? d - AIM_STEPS : d;
}

/** An analogue stick as the 8-way stick of 1987; (0, 0) inside `deadZone`. */
export function eightWay(x: number, z: number, deadZone = 0.35): { dx: Axis; dz: Axis } {
  if (Math.hypot(x, z) < deadZone) return { dx: 0, dz: 0 };
  const octant = ((Math.round(Math.atan2(x, z) / (Math.PI / 4)) % 8) + 8) % 8;
  const DX: readonly Axis[] = [0, 1, 1, 1, 0, -1, -1, -1];
  const DZ: readonly Axis[] = [1, 1, 0, -1, -1, -1, 0, 1];
  return { dx: DX[octant] ?? 0, dz: DZ[octant] ?? 0 };
}

export interface ClassicOutput {
  /** Radians, 0 = south, a whole number of 32nds of a turn. */
  aim: number;
  moveX: Axis;
  moveZ: Axis;
}

export class ClassicAim {
  /** Current aim step; he starts facing north, the way the mission runs. */
  private step = 16;
  /** Ticks the stick has been off centre. */
  private held = 0;
  private walking = false;

  get aim(): number {
    return this.step * STEP_ANGLE;
  }

  /** Face the nearest step to `angle` (taking over from another aiming device). */
  face(angle: number): void {
    this.step = ((Math.round(angle / STEP_ANGLE) % AIM_STEPS) + AIM_STEPS) % AIM_STEPS;
  }

  update(dx: Axis, dz: Axis): ClassicOutput {
    const target = stepOfDirection(dx, dz);
    if (target === null) {
      this.held = 0;
      this.walking = false;
      return { aim: this.aim, moveX: 0, moveZ: 0 };
    }
    let away = stepsBetween(this.step, target);
    // Already facing that way: no tap to tell apart, so he steps off at once.
    if (this.held === 0 && away === 0) this.walking = true;
    if (this.held >= TAP_TICKS) this.walking = true;

    // The first tick of a push turns one step; a push that outlasts a tap keeps turning.
    const turnNow = this.held === 0 || (this.held >= TAP_TICKS && (this.held - TAP_TICKS) % TURN_EVERY_TICKS === 0);
    if (turnNow && away !== 0) {
      this.step = (this.step + Math.sign(away) + AIM_STEPS) % AIM_STEPS;
      away = stepsBetween(this.step, target);
    }
    this.held++;

    const go = this.walking && Math.abs(away) <= WALK_WITHIN_STEPS;
    return { aim: this.aim, moveX: go ? dx : 0, moveZ: go ? dz : 0 };
  }
}
